import type { ToastSeverity } from '../components/symphony/ui';
import type { PromoOutcome } from './promo-outcome';

/** The query the promo page hands to the home it redirects to. */
export const PROMO_OUTCOME_PARAM = 'promo';
export const PROMO_CREDITS_PARAM = 'promoCredits';
export const PROMO_FREE_HOURS_PARAM = 'promoFreeHours';

export interface PromoToast {
  /** Catalog key — the component resolves it, so the copy is localized rather than built here. */
  key: string;
  severity: ToastSeverity;
  /** Interpolation values for `key`, already numbers so the component can format them per locale. */
  values?: Record<string, number>;
}

/**
 * What to say after a promo redemption, or null to say nothing.
 *
 * The page itself is a loader and a redirect — this is the whole user-visible result of clicking a
 * campaign link, so the silent cases matter as much as the loud ones:
 *
 * - `retry` and `failed` are SILENT on purpose. Neither is the reader's problem, and neither is
 *   final: a held code is spent after onboarding, and a transient failure is retried. Telling
 *   someone "something went wrong" about bookkeeping that is still in flight invites them to
 *   re-click a link that is already working.
 * - `already` is a success to a reader, not an error — they have the credits, just not from this
 *   click. Severity `info`, never `error`.
 */
export function promoToastFor(outcome: string | undefined, credits: number, freeHours = 0): PromoToast | null {
  switch (outcome as PromoOutcome) {
    case 'redeemed':
      // Each amount is named only when it is positive: "0 credits added" reads as a failure. A
      // reward with neither (a feature-only one) falls back to the plain confirmation.
      if (credits > 0 && freeHours > 0) {
        return { key: 'promo.toast.redeemedCreditsAndFreeHours', severity: 'success', values: { credits, hours: freeHours } };
      }
      if (freeHours > 0) return { key: 'promo.toast.redeemedFreeHours', severity: 'success', values: { hours: freeHours } };
      return credits > 0
        ? { key: 'promo.toast.redeemed', severity: 'success', values: { credits } }
        : { key: 'promo.toast.redeemedNoCredits', severity: 'success' };
    case 'already':
      return { key: 'promo.toast.already', severity: 'info' };
    case 'expired':
    // "This offer has ended" is true of a used-up code too; the two stay distinct in BI, not in copy.
    case 'exhausted':
      return { key: 'promo.toast.expired', severity: 'info' };
    case 'unknown':
      return { key: 'promo.toast.invalid', severity: 'error' };
    default:
      return null;
  }
}

/** The redirect target after a redemption: home, carrying the outcome for the toast. */
export function promoHomeUrl(basePath: string, outcome: PromoOutcome, credits: number, freeHours = 0): string {
  const home = basePath || '/';
  const params = new URLSearchParams({ [PROMO_OUTCOME_PARAM]: outcome });
  if (credits > 0) params.set(PROMO_CREDITS_PARAM, String(credits));
  if (freeHours > 0) params.set(PROMO_FREE_HOURS_PARAM, String(freeHours));
  return `${home}?${params.toString()}`;
}

/** The toast's values formatted for the reader's locale, keyed as the catalog strings name them. */
export function promoToastValues(toast: PromoToast): Record<string, string> | undefined {
  if (!toast.values) return undefined;
  return Object.fromEntries(Object.entries(toast.values).map(([k, v]) => [k, v.toLocaleString()]));
}
